// Settings → Learning. The twin's fidelity dashboard.
//
// Shows how the digital twin is being built over time: the signal ledger
// (evidence captured from verdicts, document facts, interviews, persona edits),
// the alignment curve (how well the twin predicts Martin, week over week), and
// the consolidation history (each "training step"). The owner can trigger a
// consolidation here — instant in API mode, or routed to Claude Desktop in MCP.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTwinName } from '../lib/twin.js';
import { api } from '../lib/api.js';

interface LearningStats {
  signals: {
    total: number;
    pending: number;
    byKind: Record<string, number>;
    byPolarity: Record<string, number>;
  };
  alignment: {
    verified: number;
    rate: number | null;
    confidentAccuracy: number | null;
    curve: Array<{ week: string; total: number; aligned: number; rate: number | null }>;
    profileSplit: {
      withProfile: { n: number; rate: number | null };
      without: { n: number; rate: number | null };
      delta: number | null;
    };
  };
  consolidations: {
    count: number;
    lastAt: number | null;
    runs: Array<{ id: string; createdAt: number; source: string; model: string | null; signalsProcessed: number; summary: string; status: string }>;
  };
}

const KIND_LABEL: Record<string, string> = {
  analysis_verdict: 'alignment verdicts',
  document_fact: 'document facts',
  interview_answer: 'interview answers',
  persona_edit: 'persona edits',
  conversation: 'conversation',
  manual: 'manual',
};

const POLARITY_COLOR: Record<string, string> = {
  confirm: 'text-emerald-300 border-emerald-300/30',
  correct: 'text-amber border-amber/40',
  neutral: 'text-ink/55 border-ink/20',
};

export function LearningPane() {
  const qc = useQueryClient();
  const twinName = useTwinName();

  const { data, isLoading } = useQuery<{ stats: LearningStats }>({
    queryKey: ['learning', 'stats'],
    queryFn: () => api<{ stats: LearningStats }>('/api/learning/stats'),
    refetchInterval: 15_000,
  });

  const { data: cfg } = useQuery<{ llm?: { mode?: 'mcp' | 'api' } }>({
    queryKey: ['admin', 'config'],
    queryFn: () => api('/api/admin/config'),
    staleTime: 60_000,
  });
  const llmMode: 'mcp' | 'api' = cfg?.llm?.mode === 'api' ? 'api' : 'mcp';

  const consolidate = useMutation({
    mutationFn: () => api<{ ok: boolean; error?: string; consumed?: number }>('/api/learning/consolidate', { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['learning'] });
      qc.invalidateQueries({ queryKey: ['persona'] });
    },
  });

  if (isLoading || !data) {
    return <div className="text-ink/40 text-[12px]">Loading…</div>;
  }
  const s = data.stats;
  const maxBucket = Math.max(1, ...s.alignment.curve.map((c) => c.total));

  return (
    <div>
      <div className="mb-5">
        <h3 className="font-serif text-2xl text-ink m-0">How {twinName} is learning</h3>
        <p className="font-serif italic text-sm text-ink/55 mt-1 max-w-prose">
          {twinName} is built over time from evidence — alignment verdicts, document facts, interview answers,
          and your direct edits. Signals accumulate, then a consolidation step folds them into the persona.
        </p>
      </div>

      {/* Headline metrics */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Metric n={s.signals.pending} label="pending signals" accent={s.signals.pending > 0} />
        <Metric n={s.signals.total} label="total signals" />
        <Metric n={s.alignment.rate == null ? '—' : `${s.alignment.rate}%`} label="alignment rate" />
        <Metric n={s.alignment.confidentAccuracy == null ? '—' : `${s.alignment.confidentAccuracy}%`} label="confident accuracy" />
      </div>

      {/* Consolidate control */}
      <div className="mb-6 p-4 rounded-xl border border-ink/10 bg-white/[0.015]">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="font-serif text-[15px] text-ink">Consolidate now</div>
            <div className="text-[11.5px] text-ink/45 mt-0.5">
              {s.consolidations.lastAt
                ? `last run ${new Date(s.consolidations.lastAt).toLocaleDateString()} · ${s.consolidations.count} total`
                : 'never run yet'}
            </div>
          </div>
          {llmMode === 'api' ? (
            <button
              onClick={() => consolidate.mutate()}
              disabled={consolidate.isPending || s.signals.pending === 0}
              className="px-5 py-2.5 rounded-full bg-amber text-void text-[11.5px] hover:bg-particle disabled:opacity-30 flex-shrink-0">
              {consolidate.isPending ? 'Distilling…' : s.signals.pending === 0 ? 'Nothing pending' : `Fold ${s.signals.pending} signals`}
            </button>
          ) : (
            <span className="px-3 py-1.5 rounded-full border border-amber/40 text-amber/85 text-[11px] flex-shrink-0">
              MCP mode
            </span>
          )}
        </div>
        {llmMode === 'mcp' && (
          <p className="mt-3 font-serif italic text-[13px] text-ink/55 leading-snug">
            Engine is Claude Desktop. To consolidate, ask it: <span className="font-mono text-amber not-italic">"run a consolidation"</span> — it
            calls <span className="font-mono text-ink/70 not-italic">next_consolidation_batch</span> then <span className="font-mono text-ink/70 not-italic">apply_consolidation</span>.
          </p>
        )}
        {consolidate.data?.ok && (
          <p className="mt-3 font-serif italic text-[13px] text-emerald-300/80">
            Folded {consolidate.data.consumed} signals into {twinName}.
          </p>
        )}
        {consolidate.data && !consolidate.data.ok && consolidate.data.error !== 'no_pending_signals' && (
          <p className="mt-3 font-serif italic text-[13px] text-amber/80">
            {consolidate.data.error === 'no_api_key' ? 'No API key set — switch to API mode with a key, or use Claude Desktop.' : consolidate.data.error}
          </p>
        )}
      </div>

      <TwinMLSection />

      {/* Signal breakdown */}
      <Section label="Signal ledger">
        <div className="flex flex-wrap gap-2 mb-3">
          {Object.entries(s.signals.byPolarity).map(([pol, n]) => (
            <span key={pol} className={`px-2.5 py-1 rounded-full border text-[11.5px] ${POLARITY_COLOR[pol] ?? 'text-ink/55 border-ink/20'}`}>
              {pol} · {n}
            </span>
          ))}
        </div>
        <ul className="space-y-1.5">
          {Object.entries(s.signals.byKind).sort((a, b) => b[1] - a[1]).map(([kind, n]) => (
            <li key={kind} className="flex items-center gap-3">
              <span className="font-serif text-[13px] text-ink/75 w-44">{KIND_LABEL[kind] ?? kind}</span>
              <div className="flex-1 h-2 rounded-full bg-ink/10 overflow-hidden">
                <div className="h-full bg-particle/60" style={{ width: `${Math.round((n / Math.max(1, s.signals.total)) * 100)}%` }} />
              </div>
              <span className="font-mono text-[11px] text-ink/50 w-8 text-right">{n}</span>
            </li>
          ))}
          {s.signals.total === 0 && (
            <li className="font-serif italic text-ink/40 text-sm">No signals yet. They appear as you verify analyses, analyze documents, and answer interviews.</li>
          )}
        </ul>
      </Section>

      {/* Alignment curve */}
      <Section label="Learning curve (alignment by week)">
        {s.alignment.curve.length === 0 ? (
          <p className="font-serif italic text-ink/40 text-sm">No verified analyses yet — confirm a few stances in Align to start the curve.</p>
        ) : (
          <div className="flex items-end gap-2 h-32">
            {s.alignment.curve.map((c) => (
              <div key={c.week} className="flex-1 flex flex-col items-center gap-1 min-w-0" title={`${c.week}: ${c.aligned}/${c.total} aligned`}>
                <div className="w-full flex-1 flex items-end">
                  <div
                    className="w-full rounded-t bg-gradient-to-t from-amber/40 to-particle/70"
                    style={{ height: `${c.rate == null ? 2 : Math.max(4, c.rate)}%`, opacity: 0.4 + 0.6 * (c.total / maxBucket) }}
                  />
                </div>
                <span className="font-mono text-[8px] text-ink/40">{c.rate == null ? '—' : `${c.rate}%`}</span>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Psychometric profile impact (epic 71 measurement) */}
      <Section label="Psychometric profile — effect on alignment">
        <ProfileImpact split={s.alignment.profileSplit} />
      </Section>

      {/* Consolidation history */}
      <Section label="Consolidation history">
        {s.consolidations.runs.length === 0 ? (
          <p className="font-serif italic text-ink/40 text-sm">No consolidation runs yet.</p>
        ) : (
          <ul className="space-y-2">
            {s.consolidations.runs.map((r) => (
              <li key={r.id} className="px-3 py-2 rounded-lg border border-ink/[0.07]">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[11px] text-ink/45">
                    {new Date(r.createdAt).toLocaleString()} · {r.source} · {r.signalsProcessed} signals
                  </span>
                  <span className="text-[11px] text-ink/35">{r.status}</span>
                </div>
                {r.summary && <p className="mt-1 font-serif text-[13.5px] text-ink/75 leading-relaxed">{r.summary}</p>}
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

interface TwinModel {
  trainedAt: string; n: number; extractor: 'embed' | 'local'; dim: number;
  classifier: unknown | null; calibration: unknown | null;
  visibility: { tiers: string[]; n: number } | null;
  metrics: { classifierAccuracy: number | null; baseRate: number | null;
    calibrationBins: Array<{ bucket: string; predicted: number; actual: number; n: number }>;
    visibilityAccuracy: number | null };
}

// The data-driven ML layer: a classifier + calibration trained on real verdicts —
// improving with the dataset, independent of the LLM.
function TwinMLSection() {
  const qc = useQueryClient();
  const { data } = useQuery<{ model: TwinModel | null }>({
    queryKey: ['analysis', 'ml', 'status'],
    queryFn: () => api('/api/analysis/ml/status'),
  });
  const [msg, setMsg] = useState<string | null>(null);
  const train = useMutation({
    mutationFn: () => api<{ ok: boolean; reason?: string }>('/api/analysis/ml/train', { method: 'POST' }),
    onSuccess: (r) => { setMsg(r.ok ? 'Modèle ré-entraîné ✓' : (r.reason ?? 'pas assez de données')); qc.invalidateQueries({ queryKey: ['analysis', 'ml', 'status'] }); },
    onError: async (e: unknown) => { const m = e as { body?: { reason?: string } }; setMsg(m?.body?.reason ?? 'pas assez de données pour entraîner'); },
  });
  const m = data?.model;
  const lift = m?.metrics.classifierAccuracy != null && m.metrics.baseRate != null ? m.metrics.classifierAccuracy - m.metrics.baseRate : null;

  return (
    <div className="mb-6 p-4 rounded-xl border border-particle/25 bg-particle/[0.03]">
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="font-serif text-[15px] text-ink">Twin ML — learned from your verdicts</div>
          <div className="text-[11.5px] text-ink/45 mt-0.5">
            {m ? `trained ${new Date(m.trainedAt).toLocaleDateString()} · ${m.n} verdicts · ${m.extractor === 'embed' ? 'embeddings' : 'local features'}` : 'not trained yet'}
          </div>
        </div>
        <button onClick={() => { setMsg(null); train.mutate(); }} disabled={train.isPending}
          className="px-5 py-2.5 rounded-full bg-particle text-void text-[11.5px] hover:opacity-90 disabled:opacity-30 flex-shrink-0">
          {train.isPending ? 'Entraînement…' : m ? 'Ré-entraîner' : 'Entraîner'}
        </button>
      </div>

      {m && (
        <>
          <div className="grid grid-cols-3 gap-4 mt-4">
            <Metric n={m.metrics.classifierAccuracy == null ? '—' : `${m.metrics.classifierAccuracy}%`} label="classifier accuracy" accent />
            <Metric n={m.metrics.baseRate == null ? '—' : `${m.metrics.baseRate}%`} label="majority baseline" />
            <Metric n={lift == null ? '—' : `${lift >= 0 ? '+' : ''}${lift} pts`} label="lift vs baseline" accent={Boolean(lift && lift > 0)} />
          </div>
          {m.visibility && (
            <div className="mt-3 flex items-center gap-3 flex-wrap p-3 rounded-lg border border-sky-300/20 bg-sky-300/[0.04]">
              <span className="text-[11px] text-sky-300/80">Auto-categorization</span>
              <span className="font-mono text-[11px] text-ink/70">
                {m.metrics.visibilityAccuracy == null ? '—' : `${m.metrics.visibilityAccuracy}% accuracy`} · {m.visibility.tiers.join(' / ')} · {m.visibility.n} labels
              </span>
              <span className="font-serif italic text-[12px] text-ink/45">— new outcomes get an ML-predicted tier automatically.</span>
            </div>
          )}
          {m.metrics.calibrationBins.length > 0 && (
            <div className="mt-4">
              <div className="text-[11px] text-ink/40 mb-1.5">Calibration — predicted vs actual correctness</div>
              <div className="flex flex-col gap-1">
                {m.metrics.calibrationBins.map((b) => (
                  <div key={b.bucket} className="flex items-center gap-2 font-mono text-[10px] text-ink/60">
                    <span className="w-16 text-ink/40">{b.bucket}</span>
                    <span className="w-20">pred {b.predicted}%</span>
                    <span className="w-20 text-ink">actual {b.actual}%</span>
                    <span className="text-ink/30">n={b.n}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
      <p className="mt-3 font-serif italic text-[12.5px] text-ink/55 leading-snug">
        This is real ML, not the LLM: a classifier over the question’s features predicts your stance, and a
        calibration model turns the LLM’s confidence into a true probability. Both improve as you confirm more verdicts.
        Past verdicts are also fed back into every new prediction (verdict-RAG).
      </p>
      {msg && <p className="mt-2 text-[11.5px] text-particle">{msg}</p>}
    </div>
  );
}

// Honest with-vs-without measurement of the psychometric profile's effect on how
// well the twin predicts Martin. A natural before/after: stances predicted while
// the profile was in context vs before it existed. Small-N caveat is explicit.
function ProfileImpact({ split }: { split: LearningStats['alignment']['profileSplit'] }) {
  const { withProfile: w, without: wo, delta } = split;
  if (w.n === 0) {
    return (
      <p className="font-serif italic text-ink/40 text-sm">
        Aucune prédiction faite avec le profil pour l’instant. Active le profil (Persona → Profil psycho.),
        puis génère/confirme des stances dans Align — la comparaison se remplira au fil du temps.
      </p>
    );
  }
  const bar = (rate: number | null, cls: string) => (
    <div className="h-2 rounded-full bg-ink/10 overflow-hidden">
      <div className={`h-full ${cls}`} style={{ width: `${rate ?? 0}%` }} />
    </div>
  );
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="p-3 rounded-xl border border-particle/25 bg-particle/[0.03] space-y-1.5">
          <div className="flex items-baseline justify-between"><span className="text-[11px] text-particle/80">avec profil</span><span className="font-serif text-2xl text-ink">{w.rate == null ? '—' : `${w.rate}%`}</span></div>
          {bar(w.rate, 'bg-particle/70')}
          <div className="font-mono text-[9px] text-ink/35">{w.n} stance{w.n > 1 ? 's' : ''}</div>
        </div>
        <div className="p-3 rounded-xl border border-ink/[0.08] space-y-1.5">
          <div className="flex items-baseline justify-between"><span className="text-[11px] text-ink/45">sans profil</span><span className="font-serif text-2xl text-ink">{wo.rate == null ? '—' : `${wo.rate}%`}</span></div>
          {bar(wo.rate, 'bg-ink/40')}
          <div className="font-mono text-[9px] text-ink/35">{wo.n} stance{wo.n > 1 ? 's' : ''}</div>
        </div>
      </div>
      {delta != null && (
        <p className="font-serif text-[13px] text-ink/70">
          Écart : <b className={delta > 0 ? 'text-emerald-300' : delta < 0 ? 'text-rose-300' : 'text-ink/60'}>{delta > 0 ? '+' : ''}{delta} pts</b> d’alignement avec le profil.
        </p>
      )}
      {(w.n < 20 || wo.n < 20) && (
        <p className="font-serif italic text-[11.5px] text-ink/40">
          Échantillon encore petit — indicatif, non concluant. L’écart se stabilise à mesure que tu confirmes des stances.
        </p>
      )}
    </div>
  );
}

function Metric({ n, label, accent }: { n: number | string; label: string; accent?: boolean }) {
  return (
    <div className="text-center p-3 rounded-xl border border-ink/[0.08]">
      <div className={`font-serif text-3xl ${accent ? 'text-amber' : 'text-ink'}`}>{n}</div>
      <div className="text-[11px] text-ink/40 mt-1">{label}</div>
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section className="mt-6">
      <div className="text-[11px] text-ink/45 mb-2.5">{label}</div>
      {children}
    </section>
  );
}
